/**
 * Vazifa (eslatma) o'ng tugma menyusi: bajarilgan/bajarilmaganiga qarab (sof funksiya, test: menu.test.ts).
 * Snooze vaqtlari ham shu yerda -- "Snooze" tugmasi va menyu bir xil variantlarni ko'rsatadi.
 */
import { AlarmClock, BellPlus, Check, Copy, ExternalLink, Eye, MessageSquarePlus, Phone, PhoneCall, RotateCcw } from "lucide-react";
import { compactMenu, SEPARATOR, type MenuEntry } from "@/components/ui/context-menu";
import type { Translate } from "@/features/drivers/menu";
import type { Reminder } from "@/features/drivers/types";
import { dayjs } from "@/lib/utils";

export interface SnoozeOption {
  key: "hour" | "tomorrow" | "nextMonday";
  label: string;
  at: dayjs.Dayjs;
}

/** 1 soat keyin; ertaga 9:00 va keyingi dushanba 9:00 -- foydalanuvchi vaqt zonasida */
export function snoozeOptions(tz: string, tt: Translate, now: dayjs.Dayjs = dayjs()): SnoozeOption[] {
  const nowTz = now.tz(tz);
  return [
    { key: "hour", label: tt("snooze.hour"), at: now.add(1, "hour") },
    { key: "tomorrow", label: tt("snooze.tomorrow"), at: nowTz.add(1, "day").hour(9).minute(0).second(0).millisecond(0) },
    { key: "nextMonday", label: tt("snooze.nextMonday"), at: nowTz.add((8 - nowTz.day()) % 7 || 7, "day").hour(9).minute(0).second(0).millisecond(0) },
  ];
}

export interface TaskMenuActions {
  openDriver: () => void;
  openDriverNewTab: () => void;
  call: (phone: string) => void;
  copy: (text: string) => void;
  done: () => void;
  undo: () => void;
  snooze: (option: SnoozeOption) => void;
  logActivity: (type: "call" | "note") => void;
  addReminder: () => void;
}

/** @param t "menu" namespace; snooze -- snoozeOptions() natijasi */
export function taskMenu(r: Reminder, a: TaskMenuActions, t: Translate, snooze: SnoozeOption[]): MenuEntry[] {
  return compactMenu([
    { key: "open-driver", label: t("openDriver", { name: r.driver_name }), icon: Eye, onSelect: a.openDriver },
    { key: "open-tab", label: t("openNewTab"), icon: ExternalLink, onSelect: a.openDriverNewTab },
    SEPARATOR,
    !!r.driver_phone && { key: "call", label: t("call"), icon: Phone, hint: r.driver_phone, onSelect: () => a.call(r.driver_phone) },
    !!r.driver_phone && { key: "copy-phone", label: t("copyPhone"), icon: Copy, onSelect: () => a.copy(r.driver_phone) },
    SEPARATOR,
    !r.is_done && { key: "done", label: t("task.markDone"), icon: Check, onSelect: a.done },
    !r.is_done && {
      key: "snooze",
      label: t("task.snooze"),
      icon: AlarmClock,
      items: snooze.map((o) => ({ key: `snooze-${o.key}`, label: o.label, hint: o.at.format("ddd HH:mm"), onSelect: () => a.snooze(o) })),
    },
    r.is_done && { key: "undo", label: t("task.reopen"), icon: RotateCcw, onSelect: a.undo },
    SEPARATOR,
    { key: "log-call", label: t("driver.logCall"), icon: PhoneCall, onSelect: () => a.logActivity("call") },
    { key: "add-note", label: t("driver.addNote"), icon: MessageSquarePlus, onSelect: () => a.logActivity("note") },
    { key: "add-reminder", label: t("task.addFollowUp"), icon: BellPlus, onSelect: a.addReminder },
    SEPARATOR,
    !!r.note && { key: "copy-note", label: t("task.copyNote"), icon: Copy, onSelect: () => a.copy(r.note) },
  ]);
}
